import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { VideoIcon, MicIcon, MicOffIcon, MonitorIcon, PhoneOffIcon, MessageSquareIcon, UsersIcon, SettingsIcon, FileTextIcon, PresentationIcon, CameraIcon, ActivityIcon, MusicIcon, BriefcaseIcon, LayoutGridIcon, GlobeIcon, TrendingUpIcon } from 'lucide-react';
import { RoomControls } from './RoomControls';
import { CCTVView } from './CCTVView';
import { PresentationsView } from './PresentationsView';
import { StockWidget } from './StockWidget';
import { MusicPlayer } from './MusicPlayer';
type MeetingTab = 'overview' | 'presentations' | 'security' | 'room' | 'music';
const visitorDecks = [{
  id: 1,
  title: 'Motherson Group — Company Overview',
  subtitle: 'Corporate Profile 2025',
  slides: 38,
  color: '#C9A84C',
  icon: <BriefcaseIcon className="w-5 h-5" data-id="element-123" />
}, {
  id: 2,
  title: 'Investor Relations Deck',
  subtitle: 'Q3 FY2025 Results',
  slides: 52,
  color: '#60A5FA',
  icon: <TrendingUpIcon className="w-5 h-5" data-id="element-124" />
}, {
  id: 3,
  title: 'Global Operations',
  subtitle: '41 countries · 150+ plants',
  slides: 28,
  color: '#34D399',
  icon: <GlobeIcon className="w-5 h-5" data-id="element-125" />
}, {
  id: 4,
  title: 'Sustainability Report 2025',
  subtitle: 'ESG & Green Initiatives',
  slides: 44,
  color: '#A78BFA',
  icon: <LayoutGridIcon className="w-5 h-5" data-id="element-126" />
}];
export function MeetingDashboard() {
  const [isMuted, setIsMuted] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [activeTab, setActiveTab] = useState<MeetingTab>('overview');
  const [isInCall, setIsInCall] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const handleStartCall = () => {
    setIsInCall(true);
    const t = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(t);
  };
  const formatElapsed = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor(s % 3600 / 60);
    const sec = s % 60;
    return h > 0 ? `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}` : `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };
  const tabs = [{
    id: 'overview' as MeetingTab,
    label: 'Overview',
    icon: <LayoutGridIcon className="w-4 h-4" data-id="element-127" />
  }, {
    id: 'presentations' as MeetingTab,
    label: 'Presentations',
    icon: <PresentationIcon className="w-4 h-4" data-id="element-128" />
  }, {
    id: 'music' as MeetingTab,
    label: 'Ambience',
    icon: <MusicIcon className="w-4 h-4" data-id="element-129" />
  }, {
    id: 'room' as MeetingTab,
    label: 'Room',
    icon: <SettingsIcon className="w-4 h-4" data-id="element-130" />
  }, {
    id: 'security' as MeetingTab,
    label: 'Security',
    icon: <CameraIcon className="w-4 h-4" data-id="element-131" />
  }];
  const meetingControls = [{
    id: 'mute',
    icon: isMuted ? <MicOffIcon className="w-5 h-5" data-id="element-132" /> : <MicIcon className="w-5 h-5" data-id="element-133" />,
    label: isMuted ? 'Unmute' : 'Mute',
    active: !isMuted,
    onClick: () => setIsMuted(!isMuted),
    color: isMuted ? '#EF4444' : '#C9A84C'
  }, {
    id: 'video',
    icon: <VideoIcon className="w-5 h-5" data-id="element-134" />,
    label: 'Camera',
    active: true,
    color: '#C9A84C'
  }, {
    id: 'share',
    icon: <MonitorIcon className="w-5 h-5" data-id="element-135" />,
    label: 'Share Screen',
    active: false,
    color: '#60A5FA'
  }, {
    id: 'record',
    icon: <div className="w-5 h-5" data-id="element-136" />,
    label: isRecording ? 'Stop Rec' : 'Record',
    active: isRecording,
    onClick: () => setIsRecording(!isRecording),
    color: isRecording ? '#EF4444' : '#C9A84C'
  }, {
    id: 'transcript',
    icon: <FileTextIcon className="w-5 h-5" data-id="element-137" />,
    label: 'Transcribe',
    active: false,
    color: '#A78BFA'
  }, {
    id: 'chat',
    icon: <MessageSquareIcon className="w-5 h-5" data-id="element-138" />,
    label: 'Chat',
    active: false,
    color: '#34D399'
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="flex-1 flex gap-5 p-5 overflow-hidden" data-id="element-139">
      {/* ── LEFT PANEL ── */}
      <div className="w-[360px] flex-shrink-0 flex flex-col gap-4 overflow-y-auto" data-id="element-140">
        {/* Teams Meeting Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="rounded-2xl p-5 flex-shrink-0" style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(201,168,76,0.2)'
      }} data-id="element-141">
          <div className="flex items-center gap-3 mb-4" data-id="element-142">
            <div className="w-9 h-9 rounded-xl bg-[rgba(139,92,246,0.15)] flex items-center justify-center" data-id="element-143">
              <VideoIcon className="w-4 h-4 text-purple-400" data-id="element-144" />
            </div>
            <div data-id="element-145">
              <h3 className="text-base font-semibold text-white" style={{
              fontFamily: "'Playfair Display', serif"
            }} data-id="element-146">
                Teams Meeting
              </h3>
              <p className="text-xs text-gray-400" data-id="element-147">
                {isInCall ? 'In progress' : 'Ready to start'}
              </p>
            </div>
          </div>

          {!isInCall ? <div className="space-y-2.5" data-id="element-148">
              <button onClick={handleStartCall} className="w-full h-12 rounded-xl bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-semibold text-sm transition-all shadow-lg shadow-purple-500/20 flex items-center justify-center gap-2" data-id="element-149">
                <VideoIcon className="w-4 h-4" data-id="element-150" /> Start Instant Meeting
              </button>
              <button className="w-full h-12 rounded-xl bg-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.08)] text-gray-300 font-medium text-sm transition-colors flex items-center justify-center gap-2" data-id="element-151">
                <UsersIcon className="w-4 h-4" data-id="element-152" /> Join Scheduled Meeting
              </button>
            </div> : <div className="space-y-3" data-id="element-153">
              <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-[rgba(139,92,246,0.1)] border border-[rgba(139,92,246,0.2)]" data-id="element-154">
                <span className="text-sm text-purple-300" data-id="element-155">Duration</span>
                <span className="text-lg font-mono text-white" data-id="element-156">
                  {formatElapsed(elapsed)}
                </span>
              </div>
              {isRecording && <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.2)]" data-id="element-157">
                  <div className="w-4 h-4 text-red-500 animate-pulse" data-id="element-158" />
                  <span className="text-sm text-red-400" data-id="element-159">
                    Recording in progress
                  </span>
                </div>}
              <button onClick={() => {
            setIsInCall(false);
            setElapsed(0);
          }} className="w-full h-12 rounded-xl bg-red-600 hover:bg-red-500 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2" data-id="element-160">
                <PhoneOffIcon className="w-4 h-4" data-id="element-161" /> End Meeting
              </button>
            </div>}
        </motion.div>

        {/* Meeting Controls Grid */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.08
      }} className="rounded-2xl p-4 flex-shrink-0" style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(201,168,76,0.2)'
      }} data-id="element-162">
          <p className="text-xs font-medium text-gray-500 mb-3 px-1" data-id="element-163">
            Meeting Controls
          </p>
          <div className="grid grid-cols-3 gap-2" data-id="element-164">
            {meetingControls.map(ctrl => <button key={ctrl.id} onClick={ctrl.onClick} className="p-3 rounded-xl transition-all flex flex-col items-center gap-2" style={ctrl.active ? {
            backgroundColor: `${ctrl.color}15`,
            border: `1px solid ${ctrl.color}40`
          } : {
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid transparent'
          }} data-id="element-165">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
              backgroundColor: ctrl.active ? `${ctrl.color}25` : 'rgba(255,255,255,0.06)',
              color: ctrl.active ? ctrl.color : '#9CA3AF'
            }} data-id="element-166">
                  {ctrl.icon}
                </div>
                <span className={`text-xs font-medium ${ctrl.active ? 'text-white' : 'text-gray-500'}`} data-id="element-167">
                  {ctrl.label}
                </span>
              </button>)}
          </div>
        </motion.div>

        {/* Participants */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.16
      }} className="rounded-2xl p-4 flex-shrink-0" style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(201,168,76,0.2)'
      }} data-id="element-168">
          <div className="flex items-center justify-between mb-3" data-id="element-169">
            <p className="text-xs font-medium text-gray-500" data-id="element-170">In This Room</p>
            <span className="text-xs text-[#C9A84C]" data-id="element-171">3 people</span>
          </div>
          <div className="space-y-2" data-id="element-172">
            {[{
            name: 'Akshay Gupta',
            role: 'Host · CEO',
            color: '#C9A84C'
          }, {
            name: 'Priya Sharma',
            role: 'Guest',
            color: '#60A5FA'
          }, {
            name: 'Amit Patel',
            role: 'Guest',
            color: '#34D399'
          }].map((p, i) => <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl bg-[rgba(255,255,255,0.02)]" data-id="element-173">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{
              backgroundColor: `${p.color}20`,
              color: p.color
            }} data-id="element-174">
                  {p.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div className="flex-1 min-w-0" data-id="element-175">
                  <p className="text-sm text-white truncate" data-id="element-176">{p.name}</p>
                  <p className="text-xs text-gray-500" data-id="element-177">{p.role}</p>
                </div>
                {i === 0 && <div className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" data-id="element-178" />}
              </div>)}
          </div>
        </motion.div>

        {/* Stock Widget */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.24
      }} className="flex-shrink-0" data-id="element-179">
          <StockWidget data-id="element-180" />
        </motion.div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div className="flex-1 flex flex-col gap-4 min-w-0 overflow-hidden" data-id="element-181">
        {/* Tab Navigation */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex gap-2 flex-shrink-0" data-id="element-182">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${activeTab === tab.id ? 'bg-[rgba(201,168,76,0.18)] text-[#C9A84C] border border-[rgba(201,168,76,0.3)]' : 'bg-[rgba(255,255,255,0.03)] text-gray-400 hover:bg-[rgba(255,255,255,0.06)] border border-transparent'}`} data-id="element-183">
              {tab.icon}
              {tab.label}
            </button>)}
        </motion.div>

        {/* Tab Content */}
        <div className="flex-1 overflow-hidden" data-id="element-184">
          <AnimatePresence mode="wait" data-id="element-185">
            {activeTab === 'overview' && <motion.div key="overview" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="h-full flex flex-col gap-4" data-id="element-186">
                {/* Visitor Decks */}
                <div className="rounded-2xl p-5 flex-shrink-0" style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(201,168,76,0.2)'
            }} data-id="element-187">
                  <div className="flex items-center justify-between mb-4" data-id="element-188">
                    <div className="flex items-center gap-3" data-id="element-189">
                      <div className="w-9 h-9 rounded-xl bg-[rgba(201,168,76,0.15)] flex items-center justify-center" data-id="element-190">
                        <BriefcaseIcon className="w-4 h-4 text-[#C9A84C]" data-id="element-191" />
                      </div>
                      <div data-id="element-192">
                        <h3 className="text-base font-semibold text-white" style={{
                      fontFamily: "'Playfair Display', serif"
                    }} data-id="element-193">
                          Motherson Group — Visitor Decks
                        </h3>
                        <p className="text-xs text-gray-400" data-id="element-194">
                          Ready to present
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3" data-id="element-195">
                    {visitorDecks.map((deck, i) => <motion.button key={deck.id} initial={{
                  opacity: 0,
                  scale: 0.97
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  delay: 0.06 * i
                }} className="flex items-center gap-3 p-4 rounded-xl text-left transition-all hover:scale-[1.01]" style={{
                  background: `${deck.color}0D`,
                  border: `1px solid ${deck.color}30`
                }} data-id="element-196">
                        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{
                    backgroundColor: `${deck.color}20`,
                    color: deck.color
                  }} data-id="element-197">
                          {deck.icon}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-198">
                          <p className="text-sm font-semibold text-white truncate" data-id="element-199">
                            {deck.title}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5 truncate" data-id="element-200">
                            {deck.subtitle}
                          </p>
                          <p className="text-xs mt-1" style={{
                      color: deck.color
                    }} data-id="element-201">
                            {deck.slides} slides
                          </p>
                        </div>
                      </motion.button>)}
                  </div>
                </div>

                {/* Visitor Options Grid */}
                <div className="rounded-2xl p-5 flex-1" style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(201,168,76,0.2)'
            }} data-id="element-202">
                  <h3 className="text-sm font-semibold text-gray-400 mb-4" data-id="element-203">
                    Visitor Experience
                  </h3>
                  <div className="grid grid-cols-3 gap-3" data-id="element-204">
                    {[{
                  label: 'Refreshments',
                  sub: 'Order for guests',
                  color: '#C9A84C',
                  emoji: '☕'
                }, {
                  label: 'Ambient Music',
                  sub: 'Set the mood',
                  color: '#60A5FA',
                  emoji: '🎵',
                  onClick: () => setActiveTab('music')
                }, {
                  label: 'Privacy Mode',
                  sub: 'Frosted glass on',
                  color: '#A78BFA',
                  emoji: '🔒'
                }, {
                  label: 'Room Lighting',
                  sub: 'Adjust ambience',
                  color: '#FBBF24',
                  emoji: '💡',
                  onClick: () => setActiveTab('room')
                }, {
                  label: 'Video Call',
                  sub: 'Join remotely',
                  color: '#34D399',
                  emoji: '📹'
                }, {
                  label: 'Screensaver',
                  sub: 'Ambient display',
                  color: '#F472B6',
                  emoji: '🖼️'
                }].map((opt, i) => <button key={i} onClick={opt.onClick} className="p-4 rounded-xl text-left transition-all hover:scale-[1.02]" style={{
                  background: `${opt.color}0D`,
                  border: `1px solid ${opt.color}25`
                }} data-id="element-205">
                        <div className="text-2xl mb-2" data-id="element-206">{opt.emoji}</div>
                        <p className="text-sm font-semibold text-white" data-id="element-207">
                          {opt.label}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5" data-id="element-208">
                          {opt.sub}
                        </p>
                      </button>)}
                  </div>
                </div>
              </motion.div>}

            {activeTab === 'presentations' && <motion.div key="presentations" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="h-full" data-id="element-209">
                <PresentationsView data-id="element-210" />
              </motion.div>}

            {activeTab === 'music' && <motion.div key="music" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="h-full overflow-y-auto" data-id="element-211">
                <MusicPlayer data-id="element-212" />
              </motion.div>}

            {activeTab === 'room' && <motion.div key="room" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="h-full" data-id="element-213">
                <RoomControls data-id="element-214" />
              </motion.div>}

            {activeTab === 'security' && <motion.div key="security" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="h-full" data-id="element-215">
                <CCTVView data-id="element-216" />
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>;
}